import React, { useState, useEffect, useCallback } from "react"
import { DataGrid } from "@mui/x-data-grid"
import Alert from "@mui/material/Alert"

// ─── Column definitions are static – defined outside the component so they
// are never re-created on each render, which would cause DataGrid to remount.
const COLUMNS = [
    { field: "short_name",                   headerName: "Propriété",                          flex: 1 },
    { field: "made_on",                      headerName: "Date",                               flex: 1 },
    { field: "energy_consumption_category",  headerName: "Consommation énergétique",            flex: 2 },
    { field: "greenhouse_gas_category",      headerName: "Emissions de gaz à effet de serre",  flex: 2 },
]

// ─── Shape rows coming from Supabase into what DataGrid expects.
// Supabase's PostgREST join syntax returns the related property as a nested
// object, so we flatten it here rather than in SQL.
function shapeRows(data) {
    return (data || []).map((row) => ({
        id:                             row.id,
        short_name:                     row.property?.short_name ?? "—",
        made_on:                        row.made_on,
        energy_consumption_category:    row.energy_consumption_category,
        greenhouse_gas_category:        row.greenhouse_gas_category,
    }))
}

export default function DPETable({ supabase, selectedDPEId, setSelectedDPEId }) {

    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [fetchError, setFetchError] = useState(null)

    // ── Data fetching ────────────────────────────────────────────────────────
    // We use Supabase's PostgREST foreign-key join (`property:property_id(...)`)
    // instead of an in-memory SQL JOIN, which keeps data logic in one place and
    // avoids the overhead of a local SQLite engine.
    const fetchRows = useCallback(async ({ showRefreshing = false } = {}) => {
        if (showRefreshing) setLoading(true)

        const { data, error } = await supabase
            .from("dpe")
            .select(`
                id,
                made_on,
                energy_consumption_category,
                greenhouse_gas_category,
                property:property_id (
                    short_name
                )
            `)
            .order("id")

        if (error) {
            console.error("DPETable – fetch error:", error)
            setFetchError("Impossible de charger les DPE : " + (error.message ?? error.code))
        } else {
            setFetchError(null)
            setRows(shapeRows(data))
        }

        setLoading(false)
    }, [supabase])

    // Initial load on mount
    useEffect(() => {
        fetchRows()
    }, [fetchRows])

    // ── Real-time subscription ───────────────────────────────────────────────
    // Rather than maintaining a local SQLite mirror, we subscribe to Supabase
    // postgres_changes. On any INSERT/UPDATE/DELETE on the `dpe` table we
    // trigger a background re-fetch. Because the JOIN data lives in Postgres,
    // a re-fetch is simpler and more reliable than patching rows locally.
    // The `showRefreshing: false` flag keeps the DataGrid visible (with its
    // built-in `loading` overlay) rather than swapping in the skeleton again.
    useEffect(() => {
        const channel = supabase
            .channel("dpe_realtime")
            .on(
                "postgres_changes",
                { event: "*", schema: "public", table: "dpe" },
                () => fetchRows({ showRefreshing: true })
            )
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [supabase, fetchRows])

    // ── Render ───────────────────────────────────────────────────────────────

    if (fetchError) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {fetchError}
            </Alert>
        )
    }

    return (
        <DataGrid
            autoHeight
            loading={loading}
            checkboxSelection
            disableMultipleRowSelection
            rows={rows}
            columns={COLUMNS}
            // Controlled selection so deselecting via the form (onSuccess) is
            // reflected in the table without requiring a callback chain.
            rowSelectionModel={selectedDPEId ? [selectedDPEId] : []}
            onRowSelectionModelChange={(model) => {
                setSelectedDPEId(model.length ? model[0] : null)
            }}
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            initialState={{
                pagination: { paginationModel: { pageSize: 20 } },
            }}
            pageSizeOptions={[10, 20, 50]}
            density="compact"
        />
    )
}